import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/privacidade")({
  head: () => ({
    meta: [
      { title: "Política de Privacidade — Rota Norte Express" },
      { name: "description", content: "Como a Rota Norte Express coleta, utiliza e protege seus dados pessoais conforme a LGPD." },
      { property: "og:title", content: "Política de Privacidade — Rota Norte Express" },
      { property: "og:description", content: "Como tratamos seus dados pessoais com transparência e segurança, conforme a LGPD." },
      { property: "og:url", content: "https://rotanortexpress.com.br/privacidade" },
    ],
    links: [{ rel: "canonical", href: "https://rotanortexpress.com.br/privacidade" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <main className="min-h-screen bg-gradient-to-b from-white via-[#F4F7FC] to-white text-foreground">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-dark/70 hover:text-brand-dark transition-colors">
          <ArrowLeft className="h-4 w-4" /> Voltar para o site
        </Link>
        <h1 className="mt-8 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-brand-dark">Política de Privacidade</h1>
        <p className="mt-3 text-sm text-foreground/60">Última atualização: 28 de junho de 2026</p>

        <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-foreground/80">
          <section>
            <p>A Rota Norte Express respeita sua privacidade e está comprometida em proteger seus dados pessoais. Esta Política descreve como coletamos, utilizamos, armazenamos e protegemos suas informações, em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018 — LGPD).</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-brand-dark">1. Dados que coletamos</h2>
            <ul className="mt-3 list-disc pl-5 space-y-2">
              <li><strong>Dados de identificação:</strong> nome completo, CPF e RG quando necessários para embarque ou envio de encomendas.</li>
              <li><strong>Dados de contato:</strong> telefone, WhatsApp e e-mail.</li>
              <li><strong>Dados de transporte:</strong> origem, destino, datas, descrição da encomenda e dados do destinatário.</li>
              <li><strong>Dados de navegação:</strong> endereço IP, tipo de dispositivo, páginas visitadas e cookies (ver item 6).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-brand-dark">2. Como utilizamos seus dados</h2>
            <ul className="mt-3 list-disc pl-5 space-y-2">
              <li>Para realizar reservas, embarques e entregas de encomendas.</li>
              <li>Para comunicação operacional (confirmações, atualizações, suporte).</li>
              <li>Para cumprir obrigações legais e regulatórias.</li>
              <li>Para melhorar a experiência no site e em nossos serviços.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-brand-dark">3. Compartilhamento de dados</h2>
            <p className="mt-3">Não vendemos seus dados. Compartilhamos apenas o necessário com motoristas e parceiros operacionais envolvidos na sua viagem ou na entrega da sua encomenda, e com autoridades quando exigido por lei.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-brand-dark">4. Armazenamento e segurança</h2>
            <p className="mt-3">Adotamos medidas técnicas e organizacionais para proteger seus dados contra acesso não autorizado, perda ou divulgação indevida. As informações são mantidas pelo tempo necessário ao cumprimento das finalidades descritas e às obrigações legais aplicáveis.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-brand-dark">5. Seus direitos (LGPD)</h2>
            <p className="mt-3">Você pode, a qualquer momento, solicitar:</p>
            <ul className="mt-3 list-disc pl-5 space-y-2">
              <li>Confirmação da existência de tratamento de seus dados;</li>
              <li>Acesso, correção ou atualização das informações;</li>
              <li>Anonimização, bloqueio ou eliminação de dados desnecessários;</li>
              <li>Portabilidade dos dados;</li>
              <li>Revogação do consentimento.</li>
            </ul>
            <p className="mt-3">As solicitações devem ser enviadas para <a className="text-brand-dark font-semibold hover:underline" href="mailto:contato@rotanortexpress.com.br">contato@rotanortexpress.com.br</a>.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-brand-dark">6. Cookies</h2>
            <p className="mt-3">Utilizamos cookies essenciais para o funcionamento do site e cookies de análise para entender como ele é utilizado e melhorá-lo. Ao acessar o site pela primeira vez, você pode aceitar ou recusar os cookies não essenciais por meio do banner exibido. Sua preferência pode ser alterada limpando os dados do navegador.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-brand-dark">7. Alterações nesta política</h2>
            <p className="mt-3">Podemos atualizar esta Política periodicamente. A versão mais recente estará sempre disponível nesta página.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-brand-dark">8. Contato</h2>
            <p className="mt-3">Encarregado pelo tratamento de dados (DPO): <a className="text-brand-dark font-semibold hover:underline" href="mailto:contato@rotanortexpress.com.br">contato@rotanortexpress.com.br</a>.</p>
          </section>
        </div>
      </div>
    </main>
  );
}
